import { type SxProps, type Theme, Tooltip } from '@mui/material'
import {
  DateCalendar,
  PickersDay,
  type PickersDayProps,
  dateRangePickerDayClasses,
} from '@mui/x-date-pickers-pro'
import { format, parse } from 'date-fns/esm'

import { useUpdateOrderCountsByDate } from '@/hooks'

import { useArchivedCalendarData } from './hooks/useArchivedCalendarData'

export type SelectionAsRecord = Record<string, number>

type Props = {
  selection: Date
  onChange: (date: Date) => void
  firstDayNotArchived: Date
}

type CustomDayProps = PickersDayProps<Date> & {
  highlightedDays?: SelectionAsRecord
}

export function ArchivedCalendar(props: Props) {
  const { selection, firstDayNotArchived, onChange } = props

  // Updates order counts at every open
  useUpdateOrderCountsByDate()

  const handleDateChange = (date: Date | null) => {
    if (date) {
      onChange(date)
    }
  }

  const { calendarData, daysWithOrders } = useArchivedCalendarData(firstDayNotArchived)

  // Parse string dates correctly
  const minDate = parse(calendarData.minDate, 'yyyyMMdd', new Date())
  const maxDate = parse(calendarData.maxDate, 'yyyyMMdd', new Date())

  return (
    <DateCalendar
      value={selection}
      onChange={handleDateChange}
      minDate={minDate}
      maxDate={maxDate}
      shouldDisableDate={date => {
        const formattedDate = format(date, 'yyyyMMdd')
        return formattedDate in calendarData.disabledDays
      }}
      // This is the key addition - prevent month navigation
      shouldDisableMonth={date => {
        // Get start of current month and start of min/max months
        const currentMonth = new Date(date.getFullYear(), date.getMonth(), 1)
        const minMonth = new Date(minDate.getFullYear(), minDate.getMonth(), 1)
        const maxMonth = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1)

        // Disable if month is before min month or after max month
        return currentMonth < minMonth || currentMonth > maxMonth
      }}
      // Add year disabling
      shouldDisableYear={date => {
        return (
          date.getFullYear() < minDate.getFullYear() || date.getFullYear() > maxDate.getFullYear()
        )
      }}
      slots={{
        day: CustomDay,
      }}
      slotProps={{
        day: {
          highlightedDays: daysWithOrders,
        } as any, // TypeScript error workaround as suggested on MUI-X docs
      }}
    />
  )
}

const CustomDay = (props: CustomDayProps) => {
  const { day, highlightedDays, disabled, ...other } = props
  const formattedDay = format(day, 'yyyyMMdd')
  const orderCount = highlightedDays?.[formattedDay] || 0
  const showDot = orderCount > 0 && !disabled
  const tooltipText = showDot ? `${orderCount} orders` : ''

  const sx: SxProps<Theme> = {
    position: 'relative',
    // Green dot
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: '50%',
      width: '3px',
      height: '3px',
      zIndex: 5,
      display: showDot ? 'block' : 'none',
      borderRadius: '50%',
      transform: 'translate(-50%, -7px)',
      background: theme => theme.vars.palette.success.light,

      [`.${dateRangePickerDayClasses.hiddenDayFiller} + &`]: {
        display: 'none',
      },
    },
    [`&.${dateRangePickerDayClasses.hiddenDayFiller}::before`]: {
      display: 'none',
    },
  }

  const dayComponent = <PickersDay {...other} disabled={disabled} sx={sx} day={day} />

  return showDot ? (
    <Tooltip title={tooltipText} arrow>
      {dayComponent}
    </Tooltip>
  ) : (
    dayComponent
  )
}
